import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, MessageCircle, RefreshCw, XCircle, Sparkles } from "lucide-react";

const STATUS_TONE = {
  "New Request":            "bg-slate-100 text-slate-700",
  "Consultation Scheduled": "bg-amber-100 text-amber-800",
  "Contacted":              "bg-sky-100 text-sky-800",
  "Proposal Sent":          "bg-violet-100 text-violet-800",
  "Payment Pending":        "bg-orange-100 text-orange-800",
  "Service Started":        "bg-blue-100 text-blue-800",
  "In Progress":            "bg-blue-100 text-blue-800",
  "Completed":              "bg-emerald-100 text-emerald-800",
  "Cancelled":              "bg-rose-100 text-rose-700",
};

export default function ClientServiceRequests() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/client/service-requests");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const cancelReq = async (id) => {
    if (!window.confirm("Cancel this consultation?")) return;
    setBusy(true);
    try {
      await api.post(`/client/service-requests/${id}/cancel`);
      toast.success("Consultation cancelled");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  };

  const rescheduleReq = async (id) => {
    const iso = window.prompt("Enter a new date & time (YYYY-MM-DD HH:MM)");
    if (!iso) return;
    try {
      const dt = new Date(iso.replace(" ", "T"));
      if (isNaN(dt.getTime())) throw new Error("Bad date");
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
      await api.post(`/client/service-requests/${id}/reschedule`, {
        new_slot_iso: dt.toISOString(),
        new_slot_local: dt.toLocaleString(),
        timezone: tz,
      });
      toast.success("Rescheduled");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reschedule failed");
    }
  };

  return (
    <AppShell>
      <div className="cq-in max-w-5xl">
        <button onClick={() => nav("/portal/services")} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-slate-800 mb-2">
          <ArrowLeft size={14} /> Back to services
        </button>
        <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Client portal</p>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">My Service Requests</h1>
          <Button onClick={() => nav("/portal/services")} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Sparkles size={14} className="mr-1" /> Start New Services
          </Button>
        </div>

        <div className="mt-6 space-y-4">
          {items.length === 0 && (
            <Card className="p-10 text-center rounded-2xl border-slate-100">
              <p className="text-slate-500 text-sm">You haven't requested any services yet.</p>
              <Button onClick={() => nav("/portal/services")} className="mt-4 bg-amber-500 hover:bg-amber-600 text-white">
                Browse the marketplace
              </Button>
            </Card>
          )}
          {items.map((r) => (
            <Card key={r.id} className="p-5 rounded-2xl border-slate-100" data-testid={`myreq-${r.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-700"}`}>
                      {r.status}
                    </span>
                    <span className="text-[11px] text-slate-400 font-semibold">
                      Requested {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  </div>
                  <p className="font-display font-black text-lg text-slate-900">
                    {(r.services || []).map((s) => s.name).join(", ")}
                  </p>
                  <div className="mt-2 text-sm text-slate-600 space-y-1">
                    {r.preferred_slot_local && (
                      <p><b>Consultation:</b> {r.preferred_slot_local} <span className="text-slate-400">({r.timezone})</span></p>
                    )}
                    {r.assigned_user_name && <p><b>Specialist:</b> {r.assigned_user_name}</p>}
                    {r.primary_goal && <p><b>Goal:</b> {r.primary_goal}</p>}
                    {r.next_step && <p><b>Next step:</b> {r.next_step}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {r.status !== "Cancelled" && r.status !== "Completed" && (
                    <>
                      <Button variant="outline" onClick={() => rescheduleReq(r.id)} disabled={busy}>
                        <RefreshCw size={13} className="mr-1" /> Reschedule
                      </Button>
                      <Button variant="outline" onClick={() => cancelReq(r.id)} disabled={busy} className="border-rose-200 text-rose-600 hover:bg-rose-50">
                        <XCircle size={13} className="mr-1" /> Cancel
                      </Button>
                    </>
                  )}
                  <Button onClick={() => nav("/portal/messages")} variant="outline">
                    <MessageCircle size={13} className="mr-1" /> Message team
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
